enum EnforceLatestPromiseStatus {
    OKAY,
    REQUEST_OUT_OF_DATE,
}

/**
 * Have you ever want to fire off a few promises but only care about the latest one? Then this is the class for you.
 */

/**
              What can happen                │               EnforceLatestPromise               │           EnforceLatestPromiseClearQueue
                                             │                                                  │
    100ms           500ms            100ms   │       100ms           500ms            100ms     │       100ms           500ms            100ms
┌──────────┐     ┌────────┐      ┌────────┐  │   ┌──────────┐     ┌────────┐      ┌────────┐    │   ┌──────────┐     ┌────────┐      ┌────────┐
│ Promise1 │     │Promise2│      │Promise3│  │   │ Promise1 │     │Promise2│      │Promise3│    │   │ Promise1 │     │Promise2│      │Promise3│
└──────────┘     └────────┘      └────────┘  │   └──────────┘     └────────┘      └────────┘    │   └──────────┘     └────────┘      └────────┘
                                             │                                                  │
             ┌────────────────┐              │                ┌────────────────┐                │                ┌────────────────┐
             │Promise1Resolves│              │                │Promise1Resolves│                │                │Promise1Removed │
             └───────┬────────┘              │                └───────┬────────┘                │                └───────┬────────┘
                     │                       │                        │                         │                        │
             ┌───────▼────────┐              │                ┌───────▼────────┐                │                ┌───────▼────────┐
             │Promise3Resolves│              │                │Promise3Resolves│                │                │Promise3Resolves│
             └───────┬────────┘              │                └───────┬────────┘                │                └───────┬────────┘
                     │                       │                        │                         │                        │
                     │                       │                        │                         │                        │
             ┌───────▼────────┐              │                ┌───────▼────────┐                │                ┌───────▼────────┐
             │Promise2Resolves│              │                │Promise2Ignored │                │                │Promise2Removed │
             └────────────────┘              │                └────────────────┘                │                └────────────────┘
          Promise 3 is 'overwritten'         │                Promise 3 is used                 │                Promise 3 is used



                                                Make sure the promise that resolves is the last       When a new promise is added to the queue
                                                          one in the queue                                remove all the other old promises
 */

abstract class AbstractEnforceLatestPromise {
    protected _request: number = 0;
    protected _lastAllowedRequest: number = -1;

    addNextPromiseToQueue<T = any>(
        promise: Promise<T>,
    ): Promise<{ status: EnforceLatestPromiseStatus; data: T }> {
        this._request++;
        const myRequest = this._request;
        return promise.then((data) => {
            if (myRequest < this._lastAllowedRequest) {
                return Promise.resolve({
                    status: EnforceLatestPromiseStatus.REQUEST_OUT_OF_DATE,
                    data,
                });
            }
            this._lastAllowedRequest = myRequest;
            return Promise.resolve({
                // This casting is to make typescript not complain about something that I think is a typescript
                // bug...
                status: EnforceLatestPromiseStatus.OKAY as EnforceLatestPromiseStatus,
                data,
            });
        });
    }

    protected clearOldPromises() {
        // basically "fake" a promise coming through (increase request counter)
        //  and then instantly resolving (set last allowed request)
        //  so that every other in flight promise is immediately out of date
        this._request++;
        this._lastAllowedRequest = this._request;
    }
}

/**
 * Allows you to manually clear the promise queue
 */
export class EnforceLatestPromise extends AbstractEnforceLatestPromise {
    /**
     * Synchronously invalidate any in-flight promise waiting to be resolved.
     * Even if the front of the queue resolves it will still be marked as out of date!
     */

    public clearOldPromises(): void {
        super.clearOldPromises();
    }
}
